import { useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Loading from "@/components/common/Loading";
import {
  selectIsAuthenticated,
  selectIsInternalUser,
  selectCurrentOrg,
  selectRestoringSession,
  selectIdentitySwitching,
  selectIsImpersonated,
  selectIsNoOrgExternalUser,
} from "@/data/selectors/auth";
import { restoreSession } from "@/data/slices/authSlice";
import useRefinerClient from "@/hooks/useRefinerClient";
import {
  clearGoogleReturnPath,
  getGoogleReturnPath,
} from "@/hooks/useGoogleAuth";

const AppLayout = lazy(
  () => import("@/components/Layouts/MainLayout/MainLayout"),
);

const AdminLayout = lazy(
  () => import("@/components/Layouts/AdminLayout/AdminLayout"),
);
const BasicAppLayout = lazy(
  () => import("@/components/Layouts/BasicAppLayout/BasicAppLayout"),
);
const LoginPage = lazy(
  () => import("@/components/pages/unauthorized/LoginPage/LoginPage"),
);
const RegistrationPage = lazy(
  () =>
    import("@/components/pages/unauthorized/RegistrationPage/RegistrationPage"),
);
const ForgotPasswordPage = lazy(
  () => import("@/components/pages/unauthorized/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(
  () => import("@/components/pages/unauthorized/ResetPasswordPage"),
);
const EmailConfirmationErrorPage = lazy(
  () =>
    import("@/components/pages/unauthorized/EmailActionPages/EmailConfirmationErrorPage"),
);
const EmailChangeErrorPage = lazy(
  () =>
    import("@/components/pages/unauthorized/EmailActionPages/EmailChangeErrorPage"),
);
const EmailLinkConfirmationPage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/EmailActionPages/EmailLinkConfirmationPage"
    ),
);
const RegistrationSuccessPage = lazy(
  () =>
    import("@/components/pages/unauthorized/EmailActionPages/RegistrationSuccessPage"),
);
const ForgotPasswordSuccessPage = lazy(
  () =>
    import("@/components/pages/unauthorized/EmailActionPages/ForgotPasswordSuccessPage"),
);
const LoginSSOPage = lazy(
  () => import("@/components/pages/unauthorized/LoginPage/LoginSSOPage"),
);
const LoginSSOUnavailablePage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/LoginPage/LoginSSOUnavailablePage"
    ),
);
const AcceptInviteError = lazy(
  () => import("@/components/pages/unauthorized/Invites/AcceptInviteErrorPage"),
);
const InviteGate = lazy(
  () => import("@/components/pages/unauthorized/Invites/AcceptInviteGate"),
);
const InviteAccepted = lazy(
  () => import("@/components/pages/unauthorized/Invites/InviteAccepted"),
);
const AccountDeleted = lazy(
  () =>
    import("@/components/pages/unauthorized/AccountDeletion/AccountDeleted"),
);
const AccountDeletionErrorPage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/AccountDeletion/AccountDeletionErrorPage"
    ),
);
const OwnershipTransferConfirmedPage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/OwnershipTransfer/OwnershipTransferConfirmedPage"
    ),
);
const OwnershipTransferConfirmationPage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/OwnershipTransfer/OwnershipTransferConfirmationPage"
    ),
);
const OwnershipTransferAcceptedPage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/OwnershipTransfer/OwnershipTransferAcceptedPage"
    ),
);
const OwnershipTransferAcceptancePage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/OwnershipTransfer/OwnershipTransferAcceptancePage"
    ),
);
const OwnershipTransferErrorPage = lazy(
  () =>
    import(
      "@/components/pages/unauthorized/OwnershipTransfer/OwnershipTransferErrorPage"
    ),
);
const DeleteUserPage = lazy(
  () => import("@/components/pages/admin/UserDeletion/DeleteUserPage"),
);

const GoogleLoginRoute = ({ isAuthenticated }) => {
  const returnPath = getGoogleReturnPath();

  useEffect(() => {
    if (isAuthenticated) clearGoogleReturnPath();
  }, [isAuthenticated]);

  return isAuthenticated ? (
    <Navigate to={returnPath} replace />
  ) : (
    <LoginPage />
  );
};

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsInternalUser);
  const currentOrg = useSelector(selectCurrentOrg);
  const isNoOrgExternalUser = useSelector(selectIsNoOrgExternalUser);
  const restoringSession = useSelector(selectRestoringSession);
  const identitySwitching = useSelector(selectIdentitySwitching);
  const isImpersonated = useSelector(selectIsImpersonated);
  const dispatch = useDispatch();
  useRefinerClient();

  useEffect(() => {
    if (!isAuthenticated && !restoringSession) {
      dispatch(restoreSession());
    }
  }, [isAuthenticated, restoringSession, dispatch]);

  return (
    <div className="App">
      <Toaster richColors position="top-right" />
      {restoringSession || identitySwitching ? (
        <Loading />
      ) : (
        <Router>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/account-deleted" element={<AccountDeleted />} />
              <Route
                path="/confirm-account-deletion"
                element={<EmailLinkConfirmationPage kind="accountDeletion" />}
              />
              <Route
                path="/confirm-email"
                element={<EmailLinkConfirmationPage kind="emailConfirmation" />}
              />
              <Route
                path="/confirm-email-change"
                element={<EmailLinkConfirmationPage kind="emailChange" />}
              />
              <Route
                path="/account-deletion-error"
                element={<AccountDeletionErrorPage />}
              />
              <Route
                path="/ownership-transfer-confirm"
                element={<OwnershipTransferConfirmationPage />}
              />
              <Route
                path="/ownership-transfer-confirmed"
                element={<OwnershipTransferConfirmedPage />}
              />
              <Route
                path="/ownership-transfer-accepted"
                element={<OwnershipTransferAcceptedPage />}
              />
              <Route
                path="/ownership-transfer-accept"
                element={<OwnershipTransferAcceptancePage />}
              />
              <Route
                path="/login-google"
                element={
                  <GoogleLoginRoute isAuthenticated={isAuthenticated} />
                }
              />
              <Route
                path="/ownership-transfer-error"
                element={<OwnershipTransferErrorPage />}
              />
              <Route
                path="/email-change-error"
                element={<EmailChangeErrorPage />}
              />

              {isAuthenticated && (
                <>
                  <Route
                    path="/login-sso"
                    element={
                      !isAdmin && !isImpersonated && currentOrg ? (
                        <LoginSSOPage />
                      ) : (
                        <LoginSSOUnavailablePage />
                      )
                    }
                  />
                  <Route
                    path="/accept-invite-error"
                    element={<AcceptInviteError />}
                  />
                  <Route path="/accept-invite" element={<InviteGate />} />
                  <Route path="/invite-accepted" element={<InviteAccepted />} />
                  {isAdmin && (
                    <>
                      <Route
                        path="/admin/users/delete"
                        element={<DeleteUserPage />}
                      />
                      <Route path="/" element={<AdminLayout />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                  )}
                  {!isAdmin && currentOrg && (
                    <>
                      <Route path="/" element={<AppLayout key={currentOrg} />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                  )}
                  {isNoOrgExternalUser && (
                    <>
                      <Route path="/" element={<BasicAppLayout />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                  )}
                </>
              )}

              {!isAuthenticated && (
                <>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/login-sso" element={<LoginPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/forgot-password-success"
                    element={<ForgotPasswordSuccessPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route path="/registration" element={<RegistrationPage />} />
                  <Route
                    path="/registration-success"
                    element={<RegistrationSuccessPage />}
                  />
                  <Route
                    path="/email-confirmation-error"
                    element={<EmailConfirmationErrorPage />}
                  />
                  <Route
                    path="/accept-invite-error"
                    element={<AcceptInviteError />}
                  />
                  <Route path="/accept-invite" element={<InviteGate />} />
                  <Route path="/invite-accepted" element={<InviteAccepted />} />
                  <Route path="*" element={<LoginPage />} />
                </>
              )}
            </Routes>
          </Suspense>
        </Router>
      )}
    </div>
  );
}

export default App;
